import { useEffect, useRef, useState } from 'react';

interface UseActiveTabOnScrollOptions {
    scrollContainerRef: React.RefObject<HTMLElement>;
    tabValues: string[];
    getSectionId: (tabValue: string) => string;
    defaultTab?: string;
    thresholdRatio?: number;
}

/**
 * Hook that automatically updates the active tab based on scroll position.
 * Finds the section whose top is closest to (but above) a threshold point in the viewport.
 */
export const useActiveTabOnScroll = ({
    scrollContainerRef,
    tabValues,
    getSectionId,
    defaultTab,
    thresholdRatio = 0.25,
}: UseActiveTabOnScrollOptions) => {
    const [activeTab, setActiveTab] = useState<string>(
        defaultTab || tabValues[0],
    );
    const isScrollingProgrammatically = useRef(false);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || tabValues.length === 0) return;

        const updateActiveTab = () => {
            if (isScrollingProgrammatically.current) return;

            const scrollTop = scrollContainer.scrollTop;
            const threshold =
                scrollTop + scrollContainer.clientHeight * thresholdRatio;
            const containerRect = scrollContainer.getBoundingClientRect();

            // Find the section whose top is closest to (but above) the threshold
            let bestTab = tabValues[0];
            let bestDistance = Infinity;

            tabValues.forEach((tab) => {
                const element = document.getElementById(getSectionId(tab));
                if (!element) return;

                const elementTop =
                    element.getBoundingClientRect().top -
                    containerRect.top +
                    scrollTop;

                const distance = Math.abs(elementTop - threshold);
                if (elementTop <= threshold && distance < bestDistance) {
                    bestDistance = distance;
                    bestTab = tab;
                }
            });

            setActiveTab(bestTab);
        };

        const timeoutId = setTimeout(updateActiveTab, 100);

        let rafId: number | null = null;
        const handleScroll = () => {
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    updateActiveTab();
                    rafId = null;
                });
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll, {
            passive: true,
        });

        return () => {
            clearTimeout(timeoutId);
            rafId && cancelAnimationFrame(rafId);
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [scrollContainerRef, tabValues, getSectionId, thresholdRatio]);

    const setActiveTabAndScroll = (tabValue: string) => {
        isScrollingProgrammatically.current = true;
        setActiveTab(tabValue);
        const sectionId = getSectionId(tabValue);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 1000);
        } else {
            isScrollingProgrammatically.current = false;
        }
    };

    return {
        activeTab,
        setActiveTab: setActiveTabAndScroll,
    };
};
